import React, { lazy, type PropsWithChildren, useEffect, useState } from 'react';
import cache from 'lscache';
import { useDispatch, useSelector } from 'react-redux';

import { languageStorageKey, storeStorageKey } from '../auth/constants';
import { selectAppLanguage } from '../../store/selectors/i18n.selectors';
import { updateAppLanguage, updateAvailableLanguages, type Locale } from '../../store/i18nSlice';
import { createLog } from '../../apis/logger';
import { LazyReact } from '../LazyReact/LazyReact';
import Preloader from '../Preloader/Preloader';
import { setupIntl } from '../../utils/i18n/translations';
import useToaster from '../../utils/hooks/useToaster';
import { useGetTranslationsQuery } from '../../apis/phrase/useGetTranslationsQuery';
import { WebServiceError } from '../../utils/apis/WebServiceError';
import { initialBreadcrumb } from '../Breadcrumb/Breadcrumb';
import { updateBreadcrumb } from '../../store/catalogSlice';
import { BACKUP_LOCALE, NAVIGATOR_LANGUAGE } from '../../constants/app';
import { selectSitePartyNumber, selectUserInfoPreferredLanguage } from '../../store/selectors/userInfo';
import { addUserInfo, type IcareSiteUserInfo } from '../../store/userInfoSlice';
import { type TranslationKey } from '../../utils/i18n/types';
import { useStartupQuery } from '../../apis/startup/startup';
import { updateStoreList } from '../../store/store-list';
import { type StoreItem } from '../../apis/startup/startup.type';
import { formatLocales } from '../../utils/utils';
import { initWorkshopState } from '../../store/workshopInterventionSlice';
import { setIcareKo } from '../../store/appSlice';
import { AntdLocaleProvider } from '../AntdLocaleProvider';

const ErrorPage = lazy(async () => ({
  default: (await import('../ErrorPage/ErrorPage')).ErrorPage,
}));
const ToolbarTop = lazy(async () => ({
  default: (await import('../ToolbarTop/ToolbarTopErrorPage')).ToolbarTop,
}));

const logger = createLog('StartUp');

const handledKeys: TranslationKey[] = ['error.no.available.stores', 'error.no.store.found'];

export const StartUp = ({ children }: PropsWithChildren) => {
  const storedStoreId = cache.get(storeStorageKey)?.toString();
  const sitePartyNumber = useSelector(selectSitePartyNumber);
  const preferredLanguage = useSelector(selectUserInfoPreferredLanguage);
  const appLanguage = useSelector(selectAppLanguage);
  const [startupReady, setStartupReady] = useState(false);
  const [error, setError] = useState(false);
  const [errorKey, setErrorKey] = useState<TranslationKey>();
  const [translationLocale, setTranslationLocale] = useState<Locale>();
  const translationsQuery = useGetTranslationsQuery(translationLocale);
  const { addWarningToast } = useToaster();
  const dispatch = useDispatch();
  const fetchStartup = useStartupQuery(storedStoreId, sitePartyNumber);

  useEffect(() => {
    if (error) {
      dispatch(setIcareKo(true));
    }
  }, [error]);

  useEffect(() => {
    if (fetchStartup.data) {
      void (async () => {
        try {
          const { countryId, storeInfo, storeList, availableLang, interventionStates, featureSettings } = fetchStartup.data;

          cache.set(storeStorageKey, storeInfo.id);

          const locales = formatLocales(availableLang || ['fr-FR', 'en-GB']);

          dispatch(updateAvailableLanguages(locales));

          const userInfo: IcareSiteUserInfo = {
            regionalWorkshopId: storeInfo.defaultRegionalWorkshop,
            countryId: Number(countryId),
            selectedSite: {
              address: storeInfo.address,
              allowSad: storeInfo.allowSad,
              currency: storeInfo.currency,
              id: storeInfo.id,
              name: storeInfo.name,
              shortName: storeInfo.shortName,
              maxExpressAmount: storeInfo.maxExpressAmount,
            },
            categoryWithBattery: featureSettings.isBatteryCheckActivated ? storeInfo.categoriesWithBattery : [],
          };

          dispatch(
            updateStoreList(
              storeList.map(({ id, name, countryId: countryCode, isAllowed, isRW, isRWRelated }): StoreItem => ({
                id,
                name,
                countryCode,
                isAllowed: Boolean(isAllowed),
                isRW: Boolean(isRW),
                isRWRelated: Boolean(isRWRelated),
              }))
            )
          );

          dispatch(initWorkshopState({ workshopInterventionStates: interventionStates }));

          const preferredLocale =
            preferredLanguage ??
            locales.find(locale => locale.country.toUpperCase() === userInfo.selectedSite.address.countryCode.toUpperCase())?.language ??
            'en';

          dispatch(addUserInfo(userInfo));

          const storedAuthenticationLanguage = JSON.parse(cache.get(languageStorageKey));
          const userLocale = storedAuthenticationLanguage ??
            appLanguage ??
            locales.find(
              locale =>
                locale.language.toUpperCase() === preferredLocale.toUpperCase() &&
                locale.country.toUpperCase() === userInfo.selectedSite.address.countryCode.toUpperCase()
            ) ??
            locales.find(locale => locale.language.toUpperCase() === preferredLocale.toUpperCase()) ?? {
              language: 'en',
              country: 'GB',
            };

          if (!storedAuthenticationLanguage) {
            cache.set(languageStorageKey, JSON.stringify(userLocale));
          }

          setTranslationLocale(userLocale);
        } catch (err) {
          throw new Error(err instanceof Error ? err.message : 'An unknown error occurred');
        }
      })();
    }
  }, [fetchStartup.data]);

  useEffect(() => {
    if (!fetchStartup.data || !translationLocale || !translationsQuery.isFetched) {
      return;
    }

    const { language, country } = translationLocale;
    const appLocale = `${language.toLowerCase()}-${country.toUpperCase()}`;

    void (async () => {
      try {
        if (translationsQuery.isError) {
          throw translationsQuery.error;
        }

        if (!translationsQuery.data) {
          throw new Error('Translations query completed without data');
        }

        await setupIntl({ locale: appLocale, translation: translationsQuery.data });
      } catch (err) {
        if (err instanceof WebServiceError && err.code === 'TRANSLATIONS_NOT_FOUND') {
          cache.remove(languageStorageKey);
        }

        const frenchErrorMessage = "Impossible de charger les traductions, seuls l'anglais et le français sont disponibles.";
        const englishErrorMessage = "Failed to load countries' languages. Only english and french translations are available.";
        const errorMessage = NAVIGATOR_LANGUAGE === 'fr' ? frenchErrorMessage : englishErrorMessage;

        const backupTranslation = await import(`../../utils/i18n/backupTransl/${NAVIGATOR_LANGUAGE}.json`);

        await setupIntl({ locale: appLocale, translation: backupTranslation.default });
        addWarningToast(errorMessage);
      } finally {
        try {
          const catalogStorage = sessionStorage.getItem('persist:catalog');

          if (catalogStorage) {
            const catalogState = JSON.parse(catalogStorage);
            const breadcrumb = JSON.parse(catalogState.search).breadcrumb;

            if (!breadcrumb.length) {
              dispatch(updateBreadcrumb([initialBreadcrumb()]));
            }
          }
        } catch {
          dispatch(updateBreadcrumb([initialBreadcrumb()]));
        } finally {
          dispatch(updateAppLanguage(translationLocale));
          setStartupReady(true);
        }
      }
    })();
  }, [
    addWarningToast,
    dispatch,
    fetchStartup.data,
    translationsQuery.data,
    translationsQuery.error,
    translationsQuery.isError,
    translationsQuery.isFetched,
    translationLocale,
  ]);

  useEffect(() => {
    if (fetchStartup.isError) {
      void (async () => {
        const backupTranslation = await import(`../../utils/i18n/backupTransl/${NAVIGATOR_LANGUAGE}.json`);

        await setupIntl({ locale: BACKUP_LOCALE, translation: backupTranslation.default });

        try {
          if (fetchStartup.error.status) {
            const data = fetchStartup.error;
            const replacedCode = data.code?.replaceAll(/-/, '.');
            const key = handledKeys.find(handledKey => handledKey === `error.${replacedCode}`);

            if (fetchStartup.error.status === 449) {
              setErrorKey(data.message);
            }
            if (key) {
              setErrorKey(key);
            }
          }
        } catch (e) {
          if (e instanceof Error) {
            void logger.log(e);
          }
        } finally {
          setError(true);
        }
      })();
    }
  }, [fetchStartup.isError, fetchStartup.data]);

  if (error || fetchStartup.isError) {
    return (
      <LazyReact>
        <ToolbarTop />
        <ErrorPage errorKey={errorKey} />
      </LazyReact>
    );
  }

  if (startupReady === false) {
    return <Preloader text="Loading..." fixed />;
  }

  return <AntdLocaleProvider>{children}</AntdLocaleProvider>;
};
